'use client';

import { dispatchCoveoUrlChange } from '@lib/coveo/urlChangeEvent';
import { useCallback, useEffect, useMemo, useReducer } from 'react';

function getUrl(): URL | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URL(document.location.href);
}

/**
 * Tracks the current URL and exposes push/replace, re-reading the URL on
 * browser back/forward (popstate). Adapted from Coveo's canonical SSR
 * history-router helper (their search-nextjs sample): the
 * SearchParameterManager sync uses `url` to drive engine state from the URL
 * (including on back/forward), `push` to give a user interaction its own
 * history entry, and `replace` to refine that entry as the same interaction
 * settles. Both writes broadcast COVEO_URL_CHANGE_EVENT — native pushState
 * fires no event, and subscribers outside the engine (e.g. SearchResultsBanner)
 * need the signal, same as the CSR UrlManager's writes.
 */
export function useAppHistoryRouter() {
  const [url, updateUrl] = useReducer(() => getUrl(), getUrl());

  useEffect(() => {
    window.addEventListener('popstate', updateUrl);
    return () => window.removeEventListener('popstate', updateUrl);
  }, []);

  const push = useCallback((href: string) => {
    window.history.pushState(null, document.title, href);
    dispatchCoveoUrlChange();
  }, []);

  const replace = useCallback((href: string) => {
    window.history.replaceState(null, document.title, href);
    dispatchCoveoUrlChange();
  }, []);

  return useMemo(() => ({ url, push, replace }), [url, push, replace]);
}
